import React, { useState, useEffect, useMemo } from 'react';
import { Button, Alert, Container, Row, Col, Modal, Spinner, Badge, Table, Pagination, Form, Placeholder } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FaExternalLinkAlt, FaEye } from 'react-icons/fa';
import { headers } from '../../../helpers/Requests';
import { baseURL } from '../../../helpers/baseURL';
import axios from 'axios';
import MainContainer from '../../MainContainer';
import tableStyles from '../tickets/TicketsTable.module.css';
import styles from './Invoice.module.css';
import { formatDateTime, capitalizeWord } from '../../../helpers/formatDate';
import '../embedForms/embed-form-editor.css';

const OWNER_ONLY_MESSAGE = 'Only the workspace owner can view invoice history.';
const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const statusVariant = (statusValue) => {
	switch ((statusValue || '').toLowerCase()) {
		case 'paid':
			return 'success';
		case 'unpaid':
			return 'warning';
		case 'overdue':
		case 'past_due':
			return 'danger';
		default:
			return 'secondary';
	}
};

const DetailRow = ({ label, value }) => (
	<div className={styles.detailRow}>
		<span className={styles.detailLabel}>{label}</span>
		<span className={styles.detailValue}>{value || '—'}</span>
	</div>
);

const SkeletonBar = ({ width = '70%' }) => (
	<Placeholder as="div" animation="glow" style={{ display: 'block', width }}>
		<Placeholder xs={12} className={styles.skeletonBar} />
	</Placeholder>
);

const Invoice = () => {
	const user = useSelector((state) => state?.userReducer?.user);
	const isUserLoading = useSelector((state) => state?.userReducer?.loading);
	const isUserReady = Boolean(user?.id);
	const isOwner = Boolean(user?.isOwner);

	const [invoices, setInvoices] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [detailModalOpen, setDetailModalOpen] = useState(false);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detailError, setDetailError] = useState('');
	const [invoiceDetail, setInvoiceDetail] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const totalPages = Math.max(1, Math.ceil(invoices.length / rowsPerPage));
	const paginatedInvoices = useMemo(() => {
		const start = (currentPage - 1) * rowsPerPage;
		return invoices.slice(start, start + rowsPerPage);
	}, [invoices, currentPage, rowsPerPage]);

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	const openIpaymerInvoice = (url) => {
		if (url) {
			window.open(url, '_blank', 'noopener,noreferrer');
		}
	};

	const fetchInvoiceDetail = async (invoiceId) => {
		setDetailLoading(true);
		setDetailError('');
		setInvoiceDetail(null);
		setDetailModalOpen(true);
		try {
			const response = await axios.get(`${baseURL}invoice-history/${invoiceId}`, headers);
			setInvoiceDetail(response.data);
		} catch (error) {
			setDetailError(error.response?.data?.message || 'Could not load invoice details.');
		} finally {
			setDetailLoading(false);
		}
	};

	useEffect(() => {
		if (!isUserReady || !isOwner) {
			return;
		}
		fetchInvoices();
	}, [isUserReady, isOwner]);

	const fetchInvoices = () => {
		setIsLoading(true);
		setErrorMessage('');
		axios.get(`${baseURL}invoice-history`, headers)
			.then((response) => {
				setInvoices(Array.isArray(response.data) ? response.data : []);
				setCurrentPage(1);
			})
			.catch((error) => {
				if (error.response?.data?.message) {
					setErrorMessage(error.response.data.message);
				} else {
					console.error(error);
					setErrorMessage('Something went wrong. Please try again later.');
				}
				setInvoices([]);
			})
			.finally(() => setIsLoading(false));
	};

	const closeDetailModal = () => {
		setDetailModalOpen(false);
		setInvoiceDetail(null);
		setDetailError('');
	};

	const handlePageChange = (page) => {
		setCurrentPage(Math.min(Math.max(1, page), totalPages));
	};

	const handlePerPageChange = (perPage) => {
		setRowsPerPage(perPage);
		setCurrentPage(1);
	};

	const getPageNumbers = () => {
		const pages = [];
		const maxVisible = 16;
		let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
		let end = start + maxVisible - 1;
		if (end > totalPages) {
			end = totalPages;
			start = Math.max(1, end - maxVisible + 1);
		}
		if (start > 1) {
			pages.push(
				<Pagination.Item key={1} onClick={() => handlePageChange(1)} activeLabel="">1</Pagination.Item>
			);
			if (start > 2) {
				pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
			}
		}
		for (let i = start; i <= end; i++) {
			pages.push(
				<Pagination.Item
					key={i}
					active={i === currentPage}
					onClick={() => handlePageChange(i)}
					activeLabel=""
				>
					{i}
				</Pagination.Item>
			);
		}
		if (end < totalPages) {
			if (end < totalPages - 1) {
				pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
			}
			pages.push(
				<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)} activeLabel="">
					{totalPages}
				</Pagination.Item>
			);
		}
		return pages;
	};

	const renderSkeletonRows = () => (
		Array.from({ length: 3 }).map((_, idx) => (
			<tr key={`invoice-skeleton-${idx}`} className={styles.skeletonRow} aria-hidden="true">
				<td><SkeletonBar width="55%" /></td>
				<td><SkeletonBar width="80%" /></td>
				<td><SkeletonBar width="60%" /></td>
				<td><SkeletonBar width="50%" /></td>
				<td><SkeletonBar width="50%" /></td>
				<td><SkeletonBar width="45%" /></td>
				<td><SkeletonBar width="40%" /></td>
				<td><SkeletonBar width="55%" /></td>
				<td><SkeletonBar width="50%" /></td>
				<td><SkeletonBar width="45%" /></td>
				<td className={`${tableStyles.stickyActions} ${styles.actionsCell}`}>
					<div className={styles.actionButtonsSkeleton}>
						<Placeholder as="div" animation="glow">
							<Placeholder.Button variant="secondary" className={styles.skeletonActionBtn} tabIndex={-1} />
						</Placeholder>
						<Placeholder as="div" animation="glow">
							<Placeholder.Button variant="secondary" className={styles.skeletonActionBtn} tabIndex={-1} />
						</Placeholder>
					</div>
				</td>
			</tr>
		))
	);

	const showOwnerOnlyNotice = isUserReady && !isOwner;
	const showTableLoading = !isUserReady || isUserLoading || isLoading;

	const renderTable = () => (
		<>
			<div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap ${styles.tableWrap}`}>
				<Table className={tableStyles.table} hover>
					<thead>
						<tr>
							<th className={styles.invoiceNoCell}>Invoice #</th>
							<th className={styles.planCell}>Plan / Description</th>
							<th className={styles.refCell}>Subscription Ref</th>
							<th className={styles.amountCell}>Amount Due</th>
							<th className={styles.amountCell}>Amount Paid</th>
							<th className={styles.amountCell}>Balance</th>
							<th className={styles.statusCell}>Status</th>
							<th className={styles.dateCell}>Invoice Date</th>
							<th className={styles.dateCell}>Due Date</th>
							<th className={styles.dateCell}>Paid On</th>
							<th className={`${tableStyles.stickyActions} ${styles.actionsCell}`} style={{ width: 110 }}>
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{showTableLoading ? (
							renderSkeletonRows()
						) : invoices.length === 0 ? (
							<tr>
								<td colSpan={11} className={styles.emptyCell}>
									No invoices found.
								</td>
							</tr>
						) : (
							paginatedInvoices.map((row) => (
								<tr key={row.id}>
									<td className={styles.invoiceNoCell}>
										<span className={styles.primaryText}>{row.invoice_no}</span>
									</td>
									<td className={styles.planCell}>{row.plan}</td>
									<td className={styles.refCell}>{row.subscription_ref}</td>
									<td className={styles.amountCell}>{row.amount_due_formatted}</td>
									<td className={styles.amountCell}>{row.amount_paid_formatted}</td>
									<td className={styles.amountCell}>{row.balance_due_formatted}</td>
									<td className={styles.statusCell}>
										<Badge bg={statusVariant(row.status_value)} className={styles.statusBadge}>
											{row.status}
										</Badge>
									</td>
									<td className={styles.dateCell}>{row.date}</td>
									<td className={styles.dateCell}>{row.due_date}</td>
									<td className={styles.dateCell}>{row.paid_at}</td>
									<td className={`${tableStyles.stickyActions} ${styles.actionsCell}`}>
										<div className={styles.actionButtons}>
											<Button
												variant="outline-primary"
												size="sm"
												className={tableStyles.actionBtn}
												title="View details"
												onClick={() => fetchInvoiceDetail(row.id)}
											>
												<FaEye />
											</Button>
											<Button
												variant="outline-primary"
												size="sm"
												className={tableStyles.actionBtn}
												title="Open in iPaymer"
												disabled={!row.public_url}
												onClick={() => openIpaymerInvoice(row.public_url)}
											>
												<FaExternalLinkAlt />
											</Button>
										</div>
									</td>
								</tr>
							))
						)}
					</tbody>
				</Table>
			</div>

			{!showTableLoading && invoices.length > 0 && (
				<div className={tableStyles.tableFooter}>
					<div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
						<Pagination className={tableStyles.pagination}>
							<Pagination.Prev
								disabled={currentPage === 1}
								onClick={() => handlePageChange(currentPage - 1)}
							/>
							{getPageNumbers()}
							<Pagination.Next
								disabled={currentPage === totalPages}
								onClick={() => handlePageChange(currentPage + 1)}
							/>
						</Pagination>
						<label htmlFor="invoicePerPage" className={tableStyles.perPageLabel}>Page Size:</label>
						<Form.Control
							as="select"
							id="invoicePerPage"
							value={rowsPerPage}
							onChange={(e) => handlePerPageChange(parseInt(e.target.value, 10))}
							className={tableStyles.perPage}
							style={{ width: 80 }}
						>
							{PER_PAGE_OPTIONS.map((size) => (
								<option key={size} value={size}>{size}</option>
							))}
						</Form.Control>
						<span className={tableStyles.results}>
							Showing {paginatedInvoices.length} of {invoices.length} Invoices
						</span>
					</div>
				</div>
			)}
		</>
	);

	if (showOwnerOnlyNotice) {
		return (
			<MainContainer>
				<Container fluid="sm">
					<Row className="pt-4 pb-3">
						<Col>
							<h5 className={styles.pageTitle}>Invoice History</h5>
						</Col>
					</Row>
					<Row>
						<Col>
							<Alert variant="info" className={styles.ownerOnlyNotice}>
								{OWNER_ONLY_MESSAGE}
							</Alert>
						</Col>
					</Row>
				</Container>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<Container fluid="sm">
				<Row className="pt-4 pb-3">
					<Col className="text-align-left">
						<h5 className={styles.pageTitle}>Invoice History</h5>
						<p className={styles.pageSubtitle}>
							Invoices are loaded from your iPaymer workspace for this account.
						</p>
					</Col>
				</Row>
				<Row>
					{errorMessage && (
						<Col className="mb-3">
							<Alert variant="danger">{errorMessage}</Alert>
						</Col>
					)}
					<Col className="overflow-x-auto">
						{renderTable()}
					</Col>
				</Row>
			</Container>

			<Modal show={detailModalOpen} onHide={closeDetailModal} size="lg" centered>
				<Modal.Header closeButton>
					<Modal.Title>
						{invoiceDetail?.invoice_no ? `Invoice ${invoiceDetail.invoice_no}` : 'Invoice Details'}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{detailLoading && (
						<div className={styles.modalLoading}>
							<Spinner animation="border" size="sm" /> Loading invoice…
						</div>
					)}
					{detailError && <Alert variant="danger">{detailError}</Alert>}
					{!detailLoading && invoiceDetail && (
						<>
							<div className={styles.detailGrid}>
								<DetailRow label="Status" value={
									<Badge bg={statusVariant(invoiceDetail.status_value)} className={styles.statusBadge}>{invoiceDetail.status}</Badge>
								} />
								<DetailRow label="Plan" value={invoiceDetail.plan} />
								<DetailRow label="Subscription ref" value={invoiceDetail.subscription_ref} />
								<DetailRow label="Invoice date" value={invoiceDetail.date} />
								<DetailRow label="Due date" value={invoiceDetail.due_date} />
								<DetailRow label="Paid on" value={invoiceDetail.paid_at} />
								<DetailRow label="Amount due" value={invoiceDetail.amount_due_formatted} />
								<DetailRow label="Amount paid" value={invoiceDetail.amount_paid_formatted} />
								<DetailRow label="Balance due" value={invoiceDetail.balance_due_formatted} />
								<DetailRow label="Discount" value={invoiceDetail.discount_amount_formatted} />
								<DetailRow label="Tax" value={invoiceDetail.tax_fee_formatted} />
								<DetailRow label="Pro-rate" value={invoiceDetail.pro_rate_formatted} />
								<DetailRow label="Customer" value={invoiceDetail.customer_name} />
								<DetailRow label="Email" value={invoiceDetail.customer_email} />
								<DetailRow label="Billing interval" value={invoiceDetail.recurring_interval} />
							</div>

							{invoiceDetail.note && (
								<Alert variant="light" className={styles.noteBox}>{invoiceDetail.note}</Alert>
							)}

							{invoiceDetail.line_items?.length > 0 && (
								<div className={styles.section}>
									<h6>Line items</h6>
									<Table size="sm" responsive className={styles.itemsTable}>
										<thead>
											<tr>
												<th className={styles.colPrimary}>Description</th>
												<th className={styles.colEqual}>Qty</th>
												<th className={styles.colEqual}>Unit price</th>
												<th className={styles.colEqual}>Amount</th>
											</tr>
										</thead>
										<tbody>
											{invoiceDetail.line_items.map((item, index) => (
												<tr key={index}>
													<td className={styles.colPrimary}>{item.description}</td>
													<td className={styles.colEqual}>{item.quantity}</td>
													<td className={styles.colEqual}>{item.unit_price}</td>
													<td className={styles.colEqual}>{item.amount}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</div>
							)}

							{invoiceDetail.payment_history?.length > 0 && (
								<div className={styles.section}>
									<h6>Payments</h6>
									<Table size="sm" responsive className={styles.itemsTable}>
										<thead>
											<tr>
												<th className={styles.colPrimary}>Date</th>
												<th className={styles.colEqual}>Amount</th>
												<th className={styles.colEqual}>Method</th>
												<th className={styles.colEqual}>Status</th>
											</tr>
										</thead>
										<tbody>
											{invoiceDetail.payment_history.map((payment, index) => (
												<tr key={index}>
													<td className={styles.colPrimary}>
														<span className={styles.paymentDateTime}>{formatDateTime(payment.date)}</span>
													</td>
													<td className={styles.colEqual}>{payment.amount}</td>
													<td className={styles.colEqual}>{capitalizeWord(payment.method)}</td>
													<td className={styles.colEqual}>{capitalizeWord(payment.status)}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</div>
							)}
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="light" onClick={closeDetailModal}>Close</Button>
					{invoiceDetail?.public_url && (
						<Button
							variant="primary"
							onClick={() => openIpaymerInvoice(invoiceDetail.public_url)}
						>
							<FaExternalLinkAlt className="me-1" /> Open in iPaymer
						</Button>
					)}
				</Modal.Footer>
			</Modal>
		</MainContainer>
	);
};

export default Invoice;
